import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAudio } from "@/contexts/AudioContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { ChevronLeft, Play, Pause, RotateCcw, RefreshCw, Loader2, Trash2, ImagePlus, Download } from "lucide-react";
import { toast } from "sonner";
import storyCover from "@/assets/story-cover.png";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import PageBackground from "@/components/PageBackground";
interface Story {
  id: string;
  title: string;
  content: string;
  character_name: string;
  image_url: string | null;
  audio_url: string | null;
  tempo: string;
  audio_chunks: AudioChunk[] | null;
}
interface AudioChunk {
  index: number;
  url: string | null;
  status: 'pending' | 'ready' | 'error';
}
const formatTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};
const SPEED_OPTIONS = [{
  value: 0.75,
  label: "0.75x",
  adminOnly: false
}, {
  value: 0.8,
  label: "0.8x",
  adminOnly: true
}, {
  value: 0.9,
  label: "0.9x",
  adminOnly: true
}, {
  value: 1.0,
  label: "1x",
  adminOnly: false
}, {
  value: 1.25,
  label: "1.25x",
  adminOnly: false
}, {
  value: 1.5,
  label: "1.5x",
  adminOnly: false
}];
const StoryPlayer = () => {
  const {
    slug
  } = useParams();
  const navigate = useNavigate();
  const {
    stopAllAudio,
    registerAudio,
    unregisterAudio
  } = useAudio();
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(0.9);

  // Chunked audio state
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [chunkDurations, setChunkDurations] = useState<number[]>([]);
  const [loadedChunks, setLoadedChunks] = useState<Map<number, HTMLAudioElement>>(new Map());
  const [currentChunkTime, setCurrentChunkTime] = useState(0);
  const [isChunkedMode, setIsChunkedMode] = useState(false);
  const [waitingForChunk, setWaitingForChunk] = useState(false);
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [deletingChunks, setDeletingChunks] = useState(false);
  const [regeneratingCover, setRegeneratingCover] = useState(false);
  const [userPlan, setUserPlan] = useState<string>("free");
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [storiesUsed, setStoriesUsed] = useState(0);
  const [storyLimit, setStoryLimit] = useState(3);
  const [isExpired, setIsExpired] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const currentChunkIndexRef = useRef(0);
  const playbackRateRef = useRef(1.0);
  const playChunkAtRef = useRef<((index: number) => void) | null>(null);

  // Stop any existing audio on mount
  useEffect(() => {
    stopAllAudio();
  }, [stopAllAudio]);

  // Register audio elements with global manager
  // NOTE: This effect re-runs as chunks preload; it must not unregister on every run.
  useEffect(() => {
    if (!story?.id) return;

    const allAudio: HTMLAudioElement[] = [...loadedChunks.values()];
    if (audioRef.current) allAudio.push(audioRef.current);

    if (allAudio.length > 0) {
      registerAudio(story.id, allAudio);
    }
  }, [story?.id, loadedChunks, registerAudio]);

  // Cleanup on unmount / story switch only
  // IMPORTANT: order matters — stop audio while elements are still registered, then unregister.
  useEffect(() => {
    const storyId = story?.id;
    return () => {
      stopAllAudio();
      if (storyId) unregisterAudio(storyId);
    };
  }, [story?.id, stopAllAudio, unregisterAudio]);

  // Keep refs in sync
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    currentChunkIndexRef.current = currentChunkIndex;
  }, [currentChunkIndex]);
  useEffect(() => {
    playbackRateRef.current = playbackRate;
  }, [playbackRate]);

  // Check if user is admin and fetch subscription info
  useEffect(() => {
    const checkUserStatus = async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      
      // Check admin status
      const {
        data: adminData
      } = await supabase.from('user_roles').select('role').eq('user_id', user.id).eq('role', 'admin').maybeSingle();
      setIsAdmin(!!adminData);
      
      // Fetch effective plan
      const { data: planData } = await supabase.rpc('get_effective_plan', { user_uuid: user.id });
      if (planData && planData[0]) {
        setUserPlan(planData[0].plan || 'free');
        setIsExpired(planData[0].is_expired || false);
      }
      
      // Fetch story count and limit
      const { data: countData } = await supabase.rpc('get_user_story_count', { user_uuid: user.id });
      const { data: limitData } = await supabase.rpc('get_user_story_limit', { user_uuid: user.id });
      if (countData !== null) setStoriesUsed(countData);
      if (limitData !== null) setStoryLimit(limitData);
    };
    checkUserStatus();
  }, []);
  const handleRegenerateAudio = async () => {
    if (!story) return;
    setRegenerating(true);
    // Reset chunked mode state for fresh regeneration
    setLoadedChunks(new Map());
    setChunkDurations([]);
    setCurrentChunkIndex(0);
    setCurrentChunkTime(0);
    setHasAutoStarted(false);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke("regenerate-audio", {
        body: {
          storyId: story.id
        }
      });
      if (error) throw error;
      if (data.success) {
        toast.success(`Audio regenerated: ${data.chunksReady}/${data.totalChunks} chunks ready`);
        // Story will be updated via realtime subscription
      }
    } catch (err) {
      console.error("Error regenerating audio:", err);
      toast.error("Failed to regenerate audio. Please try again.");
    } finally {
      setRegenerating(false);
    }
  };
  const handleDeleteAndRegenerateAudio = async () => {
    if (!story) return;
    setDeletingChunks(true);

    // Stop all current audio playback
    loadedChunks.forEach(audio => {
      audio.pause();
      audio.src = '';
    });
    setIsPlaying(false);
    try {
      // Delete audio files from storage
      if (story.audio_chunks && story.audio_chunks.length > 0) {
        const filesToDelete = story.audio_chunks.filter(chunk => chunk.url).map(chunk => {
          // Extract file path from URL
          const urlParts = chunk.url!.split('/storage/v1/object/public/story-audio/');
          return urlParts[1] || '';
        }).filter(path => path);
        if (filesToDelete.length > 0) {
          const {
            error: deleteError
          } = await supabase.storage.from('story-audio').remove(filesToDelete);
          if (deleteError) {
            console.error("Error deleting audio files:", deleteError);
          }
        }
      }

      // Also try to delete legacy audio_url file if it exists
      if (story.audio_url) {
        const urlParts = story.audio_url.split('/storage/v1/object/public/story-audio/');
        const legacyPath = urlParts[1];
        if (legacyPath) {
          await supabase.storage.from('story-audio').remove([legacyPath]);
        }
      }

      // Clear audio_chunks and audio_url in database
      const {
        error: updateError
      } = await supabase.from('stories').update({
        audio_chunks: [],
        audio_url: null
      }).eq('id', story.id);
      if (updateError) throw updateError;

      // Reset local state
      setLoadedChunks(new Map());
      setChunkDurations([]);
      setCurrentChunkIndex(0);
      setCurrentChunkTime(0);
      setHasAutoStarted(false);
      setIsChunkedMode(false);

      // Update local story state
      setStory(prev => prev ? {
        ...prev,
        audio_chunks: [],
        audio_url: null
      } : prev);
      toast.success("Audio chunks deleted. Click 'Generate Audio' to regenerate.");
    } catch (err) {
      console.error("Error deleting audio chunks:", err);
      toast.error("Failed to delete audio chunks.");
    } finally {
      setDeletingChunks(false);
    }
  };

  const handleRegenerateCover = async () => {
    if (!story) return;
    setRegeneratingCover(true);
    try {
      const { data, error } = await supabase.functions.invoke("regenerate-cover", {
        body: { storyId: story.id }
      });
      if (error) throw error;
      if (data.success) {
        toast.success("Cover image regenerated!");
        setStory(prev => prev ? { ...prev, image_url: data.imageUrl } : prev);
      }
    } catch (err) {
      console.error("Error regenerating cover:", err);
      toast.error("Failed to regenerate cover. Please try again.");
    } finally {
      setRegeneratingCover(false);
    }
  };

  // Handle audio download (Premium only)
  const handleDownload = async () => {
    if (userPlan !== 'premium') {
      setShowUpgradeDialog(true);
      return;
    }

    if (!story) return;
    setDownloading(true);

    try {
      toast.info("Preparing audio file...");

      if (isChunkedMode && story.audio_chunks) {
        const { data, error } = await supabase.functions.invoke('merge-audio-chunks', {
          body: { storyId: story.id }
        });

        if (error) throw error;
        if (data?.status === 'pending') {
          toast.warning("Audio still generating. Please wait until all parts are ready.");
          return;
        }
        if (!data?.audioUrl) throw new Error('Failed to get merged audio URL');

        const audioResponse = await fetch(data.audioUrl);
        const audioBlob = await audioResponse.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audioLink = document.createElement('a');
        audioLink.href = audioUrl;
        audioLink.download = `${story.title}-audio.wav`;
        audioLink.click();
        URL.revokeObjectURL(audioUrl);
      } else if (story.audio_url) {
        // Legacy single audio download
        const audioResponse = await fetch(story.audio_url);
        const audioBlob = await audioResponse.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audioLink = document.createElement('a');
        audioLink.href = audioUrl;
        audioLink.download = `${story.title}-audio.wav`;
        audioLink.click();
        URL.revokeObjectURL(audioUrl);
      }

      toast.success("Audio downloaded successfully!");
    } catch (err) {
      console.error("Error downloading audio:", err);
      toast.error("Failed to download audio. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Play chunk at specific index (fixes infinite loop by passing index directly)
  const playChunkAt = useCallback((index: number) => {
    const chunks = story?.audio_chunks;
    if (!chunks || !isChunkedMode) return;
    if (index >= chunks.length) {
      // All chunks played
      console.log("All chunks played");
      setIsPlaying(false);
      setCurrentChunkIndex(0);
      setCurrentChunkTime(0);
      return;
    }
    const chunk = chunks[index];
    const audio = loadedChunks.get(index);
    if (chunk?.status === 'ready' && audio) {
      // Chunk is ready AND loaded - play it
      console.log(`Playing chunk ${index}`);
      setCurrentChunkIndex(index);
      setCurrentChunkTime(0);
      audio.currentTime = 0;
      audio.play().catch(err => console.error("Error playing chunk:", err));
    } else if (chunk?.status === 'ready' && !audio) {
      // Chunk is ready but not yet preloaded - wait for preload
      console.log(`Chunk ${index} is ready but not loaded yet, waiting for preload...`);
      setWaitingForChunk(true);
      setCurrentChunkIndex(index);
    } else if (chunk?.status === 'pending') {
      // Chunk is still being generated - wait
      console.log(`Chunk ${index} not ready yet, waiting...`);
      setWaitingForChunk(true);
      setCurrentChunkIndex(index);
    } else if (chunk?.status === 'error') {
      // Only skip if explicitly errored
      console.log(`Chunk ${index} has error, skipping to ${index + 1}`);
      playChunkAt(index + 1);
    } else {
      // Unknown state - also wait rather than skip
      console.log(`Chunk ${index} in unknown state, waiting...`);
      setWaitingForChunk(true);
      setCurrentChunkIndex(index);
    }
  }, [story?.audio_chunks, loadedChunks, isChunkedMode]);

  // Keep playChunkAtRef in sync
  useEffect(() => {
    playChunkAtRef.current = playChunkAt;
  }, [playChunkAt]);

  // Fetch story
  useEffect(() => {
    const fetchStory = async () => {
      if (!slug) return;
      const {
        data: storyData,
        error: storyError
      } = await supabase.from("stories").select("*").eq("slug", slug).single();
      if (storyError) {
        console.error("Error fetching story:", storyError);
        setLoading(false);
        return;
      }

      // Parse audio_chunks if it's a string (JSONB comes as object already)
      const parsedStory = {
        ...storyData,
        audio_chunks: storyData.audio_chunks as unknown as AudioChunk[] | null
      };
      setStory(parsedStory);

      // Determine if we're in chunked mode
      const hasChunks = parsedStory.audio_chunks && parsedStory.audio_chunks.length > 0;
      setIsChunkedMode(hasChunks);

      // For chunked mode, keep loading until first chunk is ready
      // For legacy mode or no audio, show player immediately
      if (hasChunks) {
        const firstChunkReady = parsedStory.audio_chunks?.[0]?.status === 'ready';
        setLoading(!firstChunkReady);
      } else {
        setLoading(false);
      }
    };
    fetchStory();
  }, [slug]);

  // Subscribe to realtime updates for audio_chunks
  useEffect(() => {
    if (!story?.id) return;
    console.log("Setting up realtime subscription for story:", story.id);
    const channel = supabase.channel(`story-${story.id}`).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'stories',
      filter: `id=eq.${story.id}`
    }, payload => {
      console.log("Realtime update received:", payload);
      const newChunks = payload.new.audio_chunks as unknown as AudioChunk[] | null;
      if (newChunks) {
        setStory(prev => prev ? {
          ...prev,
          audio_chunks: newChunks
        } : prev);

        // Check if we now have chunks (transition to chunked mode)
        if (newChunks.length > 0 && !isChunkedMode) {
          setIsChunkedMode(true);
        }

        // Hide loader once first chunk is ready
        if (newChunks[0]?.status === 'ready') {
          setLoading(false);
        }
      }
    }).subscribe(status => {
      console.log("Realtime subscription status:", status);
    });
    return () => {
      console.log("Cleaning up realtime subscription");
      supabase.removeChannel(channel);
    };
  }, [story?.id, isChunkedMode]);

  // Preload audio chunks as they become ready
  useEffect(() => {
    if (!story?.audio_chunks || !isChunkedMode) return;

    story.audio_chunks.forEach((chunk) => {
      if (chunk.status === "ready" && chunk.url && !loadedChunks.has(chunk.index)) {
        console.log(`Preloading chunk ${chunk.index}: ${chunk.url}`);
        const audio = new Audio(chunk.url);
        audio.preload = "auto";
        audio.playbackRate = playbackRateRef.current;

        // Set up event handlers
        audio.onloadedmetadata = () => {
          console.log(`Chunk ${chunk.index} loaded, duration: ${audio.duration}`);
          setChunkDurations((prev) => {
            const newDurations = [...prev];
            newDurations[chunk.index] = audio.duration;
            return newDurations;
          });
        };

        // Use ref to avoid stale closure - pass next index directly
        audio.onended = () => {
          console.log(`Chunk ${chunk.index} ended, calling playChunkAt(${chunk.index + 1})`);
          playChunkAtRef.current?.(chunk.index + 1);
        };

        // Use ref for currentChunkIndex to avoid stale closure
        audio.ontimeupdate = () => {
          if (chunk.index === currentChunkIndexRef.current) {
            setCurrentChunkTime(audio.currentTime);
          }
        };
        audio.onplay = () => {
          if (chunk.index === currentChunkIndexRef.current) {
            setIsPlaying(true);
            setWaitingForChunk(false);
          }
        };
        audio.onpause = () => {
          if (chunk.index === currentChunkIndexRef.current && !audio.ended) {
            setIsPlaying(false);
          }
        };

        // IMPORTANT: register immediately so global cleanup can stop this audio
        // even if the user navigates away before the next effect runs.
        setLoadedChunks((prev) => {
          const next = new Map(prev);
          next.set(chunk.index, audio);

          if (story?.id) {
            const allAudio: HTMLAudioElement[] = [...next.values()];
            if (audioRef.current) allAudio.push(audioRef.current);
            if (allAudio.length > 0) registerAudio(story.id, allAudio);
          }

          return next;
        });
      }
    });
  }, [story?.id, story?.audio_chunks, isChunkedMode, registerAudio]);

  // Update playback rate on all loaded chunks when it changes
  useEffect(() => {
    loadedChunks.forEach(audio => {
      audio.playbackRate = playbackRate;
    });
  }, [playbackRate, loadedChunks]);

  // Update playback rate on legacy audio when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Auto-start playback when first chunk is ready
  useEffect(() => {
    if (!isChunkedMode || hasAutoStarted) return;
    const chunks = story?.audio_chunks;
    if (!chunks || chunks.length === 0) return;
    const firstChunk = chunks[0];
    if (firstChunk?.status === 'ready' && loadedChunks.has(0)) {
      console.log("First chunk ready, auto-starting playback");
      setHasAutoStarted(true);
      const audio = loadedChunks.get(0);
      if (audio) {
        audio.play().catch(err => console.error("Error auto-starting:", err));
      }
    }
  }, [story?.audio_chunks, loadedChunks, isChunkedMode, hasAutoStarted]);

  // Resume playback when a pending chunk becomes ready OR when audio gets loaded
  useEffect(() => {
    if (!waitingForChunk || !isChunkedMode) return;
    const chunks = story?.audio_chunks;
    if (!chunks) return;
    const currentChunk = chunks[currentChunkIndex];
    const audio = loadedChunks.get(currentChunkIndex);

    // Resume if chunk is ready AND audio is now loaded (removed isPlayingRef check)
    if (currentChunk?.status === 'ready' && audio) {
      console.log(`Chunk ${currentChunkIndex} is now ready and loaded, resuming playback`);
      setWaitingForChunk(false);
      setCurrentChunkTime(0);
      audio.currentTime = 0;
      audio.play().catch(err => console.error("Error resuming:", err));
    }
  }, [story?.audio_chunks, loadedChunks, currentChunkIndex, waitingForChunk, isChunkedMode]);

  // Calculate total duration from all chunks
  const totalDuration = isChunkedMode ? chunkDurations.reduce((a, b) => a + (b || 0), 0) : duration;

  // Calculate current total time across chunks
  const currentTotalTime = isChunkedMode ? chunkDurations.slice(0, currentChunkIndex).reduce((a, b) => a + (b || 0), 0) + currentChunkTime : currentTime;
  const progress = totalDuration > 0 ? currentTotalTime / totalDuration * 100 : 0;

  // Handle audio playback (legacy single audio mode)
  const togglePlayPause = () => {
    if (isChunkedMode) {
      const currentAudio = loadedChunks.get(currentChunkIndex);
      if (!currentAudio) {
        // Audio not loaded yet - trigger loading/waiting via playChunkAt
        console.log(`No audio for chunk ${currentChunkIndex}, attempting to play from current position`);
        playChunkAt(currentChunkIndex);
        return;
      }
      if (isPlaying) {
        currentAudio.pause();
        setIsPlaying(false);
      } else {
        currentAudio.play().catch(err => console.error("Error playing:", err));
        setIsPlaying(true);
      }
    } else {
      if (!audioRef.current || !story?.audio_url) return;
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };
  const handleRestart = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentChunkTime(0);
    setCurrentChunkIndex(0);
    if (isChunkedMode) {
      // Stop all chunks
      loadedChunks.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    } else if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      audioRef.current.playbackRate = playbackRate;
    }
  };
  const handleSeek = (value: number[]) => {
    const targetTime = value[0] / 100 * totalDuration;
    if (isChunkedMode) {
      // STOP ALL audio elements first to prevent parallel playback
      loadedChunks.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      let accumulated = 0;
      for (let i = 0; i < chunkDurations.length; i++) {
        const chunkDuration = chunkDurations[i] || 0;
        if (accumulated + chunkDuration >= targetTime) {
          const timeInChunk = targetTime - accumulated;

          // Set new chunk and time
          setCurrentChunkIndex(i);
          setCurrentChunkTime(timeInChunk);
          const targetAudio = loadedChunks.get(i);
          if (targetAudio) {
            targetAudio.currentTime = timeInChunk;
            if (isPlaying) {
              targetAudio.play().catch(err => console.error("Error seeking:", err));
            }
          }
          break;
        }
        accumulated += chunkDuration;
      }
    } else if (audioRef.current && duration > 0) {
      const newTime = value[0] / 100 * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };
  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  // Determine if audio is available
  const hasChunkedAudio = isChunkedMode && story?.audio_chunks && story.audio_chunks.some(c => c.status === 'ready');
  const hasLegacyAudio = !isChunkedMode && story?.audio_url;
  const hasAnyAudio = hasChunkedAudio || hasLegacyAudio;

  // Check if chunks are still being generated
  const chunksGenerating = isChunkedMode && story?.audio_chunks && story.audio_chunks.some(c => c.status === 'pending');
  const readyChunksCount = story?.audio_chunks?.filter(c => c.status === 'ready').length || 0;
  const totalChunksCount = story?.audio_chunks?.length || 0;

  // Check if ALL audio chunks are ready (for download button visibility)
  const allChunksReady = isChunkedMode 
    ? story?.audio_chunks && story.audio_chunks.length > 0 && story.audio_chunks.every(c => c.status === 'ready')
    : !!story?.audio_url;
  if (loading) {
    return <PageBackground className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </PageBackground>;
  }
  if (!story) {
    return <PageBackground className="flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Story not found</h2>
          <Button onClick={() => navigate("/library")}>Go to Library</Button>
        </div>
      </PageBackground>;
  }
  return <PageBackground className="pb-32 md:pb-32">
      <div className="container mx-auto px-4 py-4 md:py-8 pb-24 md:pb-8">
        <Button variant="ghost" onClick={() => window.history.length > 1 ? navigate(-1) : navigate("/library")} className="mb-4 md:mb-6 gap-2 -ml-2">
          <ChevronLeft className="w-4 h-4" />
          Back to Library
        </Button>

        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden shadow-md">
            {/* Cover Image Section */}
            <div className="relative bg-gradient-to-br from-primary/20 to-secondary/20 flex justify-center py-3 md:py-4">
              <img src={story.image_url || storyCover} alt={story.title} className="max-w-[85%] md:max-w-[60%] h-auto object-contain" />
              
              {/* Waiting for Chunk Overlay */}
              {waitingForChunk && <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-10">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-white/30 blur-xl animate-pulse" />
                    <Loader2 className="w-10 h-10 md:w-12 md:h-12 text-white animate-spin relative z-10 drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]" />
                  </div>
                </div>}
              
              {/* Title Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6">
                <h1 className="text-xl md:text-3xl lg:text-4xl font-semibold text-white mb-1 font-sans line-clamp-2">
                  {story.title}
                </h1>
                <p className="text-sm md:text-base text-white/90">
                  Featuring {story.character_name}
                </p>
              </div>
            </div>

            {/* Audio Controls */}
            <div className="p-4 md:p-6 space-y-3">
              {hasAnyAudio || chunksGenerating ? <>
                  {/* Generating indicator */}
                  {chunksGenerating && <div className="flex items-center justify-center gap-2 text-xs md:text-sm text-muted-foreground mb-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generating audio... ({readyChunksCount}/{totalChunksCount} parts ready)</span>
                    </div>}

                  {/* Seekbar */}
                  <div className="space-y-2">
                    <Slider value={[progress]} onValueChange={handleSeek} max={100} step={0.1} className="w-full cursor-pointer" disabled={!hasAnyAudio} />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{formatTime(currentTotalTime)}</span>
                      <span>{formatTime(totalDuration)}</span>
                    </div>
                  </div>

                  {/* Control Buttons - Mobile optimized */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button onClick={handleRestart} variant="outline" size="default" className="gap-2 flex-1 sm:flex-initial h-11" disabled={!hasAnyAudio}>
                        <RotateCcw className="w-4 h-4" />
                        <span className="sm:inline">Restart</span>
                      </Button>
                      
                      <Button onClick={togglePlayPause} size="default" className="gap-2 flex-1 sm:flex-initial h-11 sm:px-6" disabled={!hasAnyAudio}>
                        {waitingForChunk ? <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="hidden sm:inline">Buffering...</span>
                          </> : isPlaying ? <>
                            <Pause className="w-4 h-4" />
                            <span>Pause</span>
                          </> : <>
                            <Play className="w-4 h-4" />
                            <span>Play</span>
                          </>}
                      </Button>

                      {allChunksReady && (
                        <Button onClick={handleDownload} variant="outline" size="default" className="gap-2 flex-1 sm:flex-initial h-11" disabled={downloading}>
                          {downloading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4" />
                              <span className="sm:inline">Download</span>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Speed Control - Admin only */}
                  {isAdmin && (
                    <div className="flex items-center justify-center gap-1.5 md:gap-2 pt-2">
                      <span className="text-xs text-muted-foreground mr-1">Speed:</span>
                      {SPEED_OPTIONS.filter(option => !option.adminOnly || isAdmin).map(option => <Button key={option.value} onClick={() => setPlaybackRate(option.value)} variant={playbackRate === option.value ? "default" : "outline"} size="sm" className="h-9 px-3 md:h-7 md:px-2.5 text-xs min-w-[48px]">
                          {option.label}
                        </Button>)}
                    </div>
                  )}

                  {/* Admin-only chunk indicator */}
                  {isAdmin && isChunkedMode && story?.audio_chunks && story.audio_chunks.length > 0 && <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-muted-foreground font-medium">
                          Chunk Debug (Admin Only)
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleDeleteAndRegenerateAudio} disabled={deletingChunks || regenerating} variant="outline" size="sm" className="gap-1.5 h-7 text-xs text-destructive hover:text-destructive">
                            {deletingChunks ? <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Deleting...
                              </> : <>
                                <Trash2 className="w-3 h-3" />
                                Delete & Regenerate
                              </>}
                          </Button>
                          {story.audio_chunks.some(c => c.status === 'error') && <Button onClick={handleRegenerateAudio} disabled={regenerating || deletingChunks} variant="outline" size="sm" className="gap-1.5 h-7 text-xs">
                              {regenerating ? <>
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  Regenerating...
                                </> : <>
                                  <RefreshCw className="w-3 h-3" />
                                  Regenerate Audio
                                </>}
                            </Button>}
                          <Button onClick={handleRegenerateCover} disabled={regeneratingCover || deletingChunks} variant="outline" size="sm" className="gap-1.5 h-7 text-xs">
                            {regeneratingCover ? <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Generating Cover...
                              </> : <>
                                <ImagePlus className="w-3 h-3" />
                                Regenerate Cover
                              </>}
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {story.audio_chunks.map((chunk, idx) => <div key={idx} className={`
                              px-2 py-1 rounded text-xs font-mono transition-all
                              ${chunk.status === 'ready' ? idx === currentChunkIndex ? 'bg-primary text-primary-foreground ring-2 ring-primary/50' : 'bg-primary/20 text-primary' : chunk.status === 'pending' ? 'bg-muted text-muted-foreground animate-pulse' : 'bg-destructive/20 text-destructive'}
                            `}>
                            {idx + 1}
                            {idx === currentChunkIndex && isPlaying && <span className="ml-1">▶</span>}
                            {chunk.status === 'pending' && <span className="ml-1">⏳</span>}
                            {chunk.status === 'error' && <span className="ml-1">✕</span>}
                          </div>)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        Playing chunk {currentChunkIndex + 1}/{story.audio_chunks.length}
                        {chunkDurations[currentChunkIndex] && <span className="ml-2">
                            ({formatTime(currentChunkTime)}/{formatTime(chunkDurations[currentChunkIndex])})
                          </span>}
                      </div>
                    </div>}
                </> : <div className="text-center py-4 space-y-3">
                  <p className="text-muted-foreground">Audio not available</p>
                  <Button onClick={handleRegenerateAudio} disabled={regenerating} className="gap-2">
                    {regenerating ? <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating...
                      </> : <>
                        <RefreshCw className="w-4 h-4" />
                        Generate Audio
                      </>}
                  </Button>
                </div>}
            </div>
          </Card>
        </div>
      </div>

      {/* Legacy Audio Element */}
      {!isChunkedMode && story.audio_url && <audio ref={audioRef} src={story.audio_url} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleAudioEnd} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} />}

      {/* Upgrade Dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        currentPlan={userPlan}
        storiesUsed={storiesUsed}
        limit={storyLimit}
        isExpired={isExpired}
        downloadAttempt={true}
      />
    </PageBackground>;
};
export default StoryPlayer;